import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import CardFace from '../Card/CardFace';
import { PlainButton } from '../Controls';
import CardZoom, { useCardSteps } from '.';

const Sheet = styled.div`
  position: fixed;
  inset: 0;
  z-index: 55;
  display: flex;
  flex-direction: column;
  padding: calc(var(--top-clear, 0px) + 16px) 16px 16px;
  background: rgba(10, 24, 16, .96);
  overflow-y: auto;

  & h2 {
    margin: 0 0 12px;
    font-size: 18px;
  }
`;

const Grid = styled.ul`
  --card-w: 76px;
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(var(--card-w), 1fr));
  gap: 8px;
`;

const Close = styled(PlainButton)`
  align-self: flex-end;
  margin-bottom: 4px;
  font-weight: 700;
`;

/**
 * A pile's cards to look through (a discard pile in a replay), the top first; tap one to see it full size. Escape
 * closes it, or first the card open over it.
 */
const PileView = ({ title, cards, onClose }) => {
  const topFirst = [...cards].reverse();
  const steps = useCardSteps(topFirst);
  const zoomed = !!steps.zoom;

  useEffect(() => {
    if (zoomed) {
      return undefined;
    }
    const close = (event) => event.key === 'Escape' && onClose();
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [zoomed, onClose]);

  return (
    <Sheet role="dialog" aria-label={title}>
      <Close onClick={onClose}>Close</Close>
      <h2>{title} · {cards.length}</h2>
      <Grid>
        {topFirst.map((card, i) => (
          <li key={`${card.get('id')}-${i}`}>
            <PlainButton onClick={() => steps.open(i)} aria-label={card.get('name')}><CardFace card={card} /></PlainButton>
          </li>
        ))}
      </Grid>
      {steps.zoom && <CardZoom {...steps.zoom} />}
    </Sheet>
  );
};

PileView.propTypes = {
  title: PropTypes.string.isRequired,
  /** The pile bottom first, as the server keeps it. */
  cards: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default PileView;
